/**
 * Presentation helpers for the analysis queue (labels, colours, ages and action gating).
 * Pure functions over ledger run documents so they can be unit tested without Vuetify.
 */
import { isTerminalRunStatus, normalizeRunStatus } from '@/store/modules/analysisQueue';

/** Default server values (VP_HEARTBEAT_TTL_S / VP_CANCEL_GRACE_S) used until the read model exposes them. */
export const DEFAULT_HEARTBEAT_TTL_S = 600;
export const DEFAULT_CANCEL_GRACE_S = 180;

const STATUS_PRESENTATION = Object.freeze({
  QUEUED: { label: 'Queued', color: 'grey', dark: false, spinner: false },
  DISPATCHED: { label: 'Dispatched', color: 'primary', dark: true, spinner: true },
  RUNNING: { label: 'Running', color: 'primary', dark: true, spinner: true },
  CANCEL_REQUESTED: { label: 'Cancel requested', color: 'warning', dark: true, spinner: true },
  SUCCEEDED: { label: 'Succeeded', color: 'success', dark: true, spinner: false },
  FAILED: { label: 'Failed', color: 'error', dark: true, spinner: false },
  CANCELLED: { label: 'Cancelled', color: 'grey darken-2', dark: true, spinner: false },
});

export function statusPresentation(status) {
  return (
    STATUS_PRESENTATION[normalizeRunStatus(status)] || {
      label: status ? String(status) : 'Unknown',
      color: 'grey',
      dark: false,
      spinner: false,
    }
  );
}

const SOURCE_PRESENTATION = Object.freeze({
  interview: { label: 'Interview', icon: 'mdi-account-voice', route: '/interview-analysis/' },
  manual_analysis: { label: 'Video', icon: 'mdi-video', route: '/coherence-analysis/' },
  cv: { label: 'CV', icon: 'mdi-file-account', route: null },
});

export function sourcePresentation(sourceType) {
  return (
    SOURCE_PRESENTATION[sourceType] || {
      label: 'Source',
      icon: 'mdi-help-circle-outline',
      route: null,
    }
  );
}

/** Human label for the run's source: `source_label` when the read model provides it, else type + id. */
export function sourceLabel(run) {
  if (!run) return '';
  if (run.source_label) return String(run.source_label);
  const { label } = sourcePresentation(run.source_type);
  return run.source_id != null ? `${label} ${run.source_id}` : label;
}

/** Route to open the source, or null when there is no detail page (cv). */
export function sourceRoute(run) {
  if (!run || run.source_id == null) return null;
  const { route } = sourcePresentation(run.source_type);
  return route ? `${route}${encodeURIComponent(run.source_id)}` : null;
}

export function toMillis(value) {
  if (value == null || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Seconds elapsed since `value` at `now`, or null when unknown. Never negative. */
export function ageSeconds(value, now = Date.now()) {
  const ms = toMillis(value);
  if (ms == null) return null;
  return Math.max(0, Math.round((now - ms) / 1000));
}

/** Compact relative time: 'just now', '45 s ago', '3 min ago', '2 h ago', '1 d ago'. */
/** Compact size for the health strip: 1.4 GB, 130 MB, 900 kB. Null when unknown. */
export function formatBytes(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  if (value < 1000) return `${Math.round(value)} B`;
  const units = ['kB', 'MB', 'GB', 'TB'];
  let size = value / 1000;
  let unit = 0;
  while (size >= 1000 && unit < units.length - 1) {
    size /= 1000;
    unit += 1;
  }
  return `${size >= 10 ? Math.round(size) : size.toFixed(1)} ${units[unit]}`;
}

export function formatAge(value, now = Date.now()) {
  const seconds = ageSeconds(value, now);
  if (seconds == null) return '';
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** Duration between two instants: '4 m 12 s', '48 s', '1 h 03 m'. */
export function formatDuration(from, to = Date.now()) {
  const start = toMillis(from);
  const end = toMillis(to);
  if (start == null || end == null) return '';
  const total = Math.max(0, Math.round((end - start) / 1000));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes < 60) return `${minutes} m ${String(seconds).padStart(2, '0')} s`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, '0')} m`;
}

/** Last sign of life from the worker: heartbeat, else start, else dispatch time. */
export function lastSeenAt(run) {
  if (!run) return null;
  return run.heartbeat_at || run.started_at || run.dispatched_at || null;
}

export function isHeartbeatStale(run, now = Date.now(), ttlSeconds = DEFAULT_HEARTBEAT_TTL_S) {
  const seconds = ageSeconds(lastSeenAt(run), now);
  return seconds != null && seconds > ttlSeconds;
}

export function cancelGraceElapsed(run, now = Date.now(), graceSeconds = DEFAULT_CANCEL_GRACE_S) {
  const requestedAt = run?.cancel?.requested_at || null;
  const seconds = ageSeconds(requestedAt, now);
  return seconds != null && seconds >= graceSeconds;
}

/** Engine block to display: resolved (finish) wins over the static one (start). */
export function engineBlockOf(run) {
  return run?.engine_resolved || run?.engine || null;
}

const HIGHLIGHTED_ENGINES = Object.freeze(['gestures', 'coherence']);

/**
 * Chips for the run card: `gestures 2.0.3`, `coherence 1.4.2` (when known), `build <sha>` and the
 * count of other engines. Empty until the worker stamps `engine` at start.
 */
export function engineChips(run) {
  const block = engineBlockOf(run);
  if (!block) return [];
  const engines = block.engines && typeof block.engines === 'object' ? block.engines : {};
  const chips = HIGHLIGHTED_ENGINES.filter((name) => engines[name] != null).map((name) => ({
    key: name,
    text: `${name} ${engines[name]}`,
    mono: false,
  }));
  const others = Object.keys(engines).filter((name) => !HIGHLIGHTED_ENGINES.includes(name));
  if (others.length) {
    chips.push({
      key: 'others',
      text: `+${others.length} engines`,
      mono: false,
      tooltip: others.map((name) => `${name} ${engines[name]}`).join(' · '),
    });
  }
  if (block.build_sha) {
    chips.push({
      key: 'build',
      text: `build ${String(block.build_sha).slice(0, 7)}`,
      mono: true,
      dev: block.build_sha === 'dev',
    });
  }
  return chips;
}

/**
 * Which control actions apply to a run right now.
 *
 * @param {object} run ledger document
 * @param {{ now?: number, heartbeatTtlS?: number, cancelGraceS?: number, forceEnabled?: boolean }} opts
 * @returns {{ remove: boolean, cancel: boolean, force: boolean, markFailed: boolean,
 *   forceGraceElapsed: boolean, heartbeatStale: boolean }}
 */
export function availableActions(run, opts = {}) {
  const now = opts.now ?? Date.now();
  const status = normalizeRunStatus(run?.status);
  const heartbeatStale =
    (status === 'RUNNING' || status === 'CANCEL_REQUESTED') &&
    isHeartbeatStale(run, now, opts.heartbeatTtlS ?? DEFAULT_HEARTBEAT_TTL_S);
  const forceGraceElapsed = cancelGraceElapsed(
    run,
    now,
    opts.cancelGraceS ?? DEFAULT_CANCEL_GRACE_S
  );
  return {
    remove: status === 'QUEUED' || status === 'DISPATCHED',
    cancel: status === 'RUNNING' && !isTerminalRunStatus(status),
    force: status === 'CANCEL_REQUESTED' && opts.forceEnabled !== false && !run?.cancel?.forced,
    markFailed: heartbeatStale,
    forceGraceElapsed,
    heartbeatStale,
  };
}

/**
 * Snackbar text for a terminal transition of a watched run (store `notifications` entries:
 * {run_id, status, source_type, source_id, source_label}).
 * @returns {{ text: string, color: string }}
 */
export function describeRunNotification(notification) {
  const status = normalizeRunStatus(notification?.status);
  const label = sourceLabel(notification) || 'run';
  if (status === 'SUCCEEDED') return { text: `Analysis of ${label} finished`, color: 'success' };
  if (status === 'FAILED') return { text: `Analysis of ${label} failed`, color: 'error' };
  if (status === 'CANCELLED')
    return { text: `Analysis of ${label} cancelled`, color: 'grey darken-2' };
  return {
    text: `Analysis of ${label}: ${statusPresentation(status).label.toLowerCase()}`,
    color: 'info',
  };
}

/** Human message for a failed control request (axios error). */
export function describeActionError(error, action) {
  const status = error?.response?.status;
  // Node error bodies are {code, short, long} (common/errorHandler.ts); legacy ones {error, message}.
  const data = error?.response?.data;
  const serverMessage = data?.long || data?.short || data?.message || data?.error;
  if (status === 409) {
    if (action === 'cancel')
      return 'Too late to cancel: the run is already persisting its results.';
    if (action === 'remove') return 'The run has already started; use Cancel instead.';
    if (action === 'mark-failed')
      return 'The worker is still alive (recent heartbeat); nothing to mark.';
    if (action === 'force') return 'Force cancel is not allowed yet for this run.';
    if (action === 'reorder') return 'The run is no longer waiting; the lane was refreshed.';
    if (action === 'replay') return 'This video already has an analysis running; wait for it to finish.';
    return serverMessage || 'The run changed state before the action was applied.';
  }
  if (status === 403 && action === 'force') {
    return 'Force cancel is disabled on this server (VP_FORCE_CANCEL_ENABLED).';
  }
  if (status === 400 && action === 'replay') {
    return serverMessage || 'This run cannot be replayed; launch a new analysis instead.';
  }
  if (status === 404) return 'The run no longer exists.';
  return serverMessage || error?.message || 'The action failed.';
}
